import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Copy,
  Loader2,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { GenieMist } from "@/pages/agent-interfaces/live-agent/components/GenieMist";
import { usePublicActivationPack } from "@/hooks/activation";
import { resolveActivationPresentationContent } from "@/services/activation/presentationContent";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_BADGE_SRC,
  GOOGLE_PLAY_BADGE_SRC,
} from "@/constants/appStore";
import { SITE_URL } from "@/config/site";
import {
  addPrivateAccessTokenFragment,
  capturePrivateAccessToken,
} from "@/lib/privateAccessToken";
import type {
  PublicActivationChannel,
  PublicActivationDTO,
} from "@/types/activation/activation-pack";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

const CHANNEL_META: Record<string, { genieIcon: GenieIconName; cta: string }> = {
  web_widget: { genieIcon: "globe", cta: "Open chat" },
  phone: { genieIcon: "phone", cta: "Call now" },
  sms: { genieIcon: "chat", cta: "Send a text" },
  whatsapp: { genieIcon: "chat", cta: "Open WhatsApp" },
};

const SECTION_COUNT = 7;

const addPrivateAccessToken = (value: string, token: string): string => {
  try {
    const url = new URL(value, SITE_URL);
    if (
      url.origin === new URL(SITE_URL).origin &&
      url.pathname.startsWith("/live-agent/")
    ) {
      addPrivateAccessTokenFragment(url, token);
    }
    return url.toString();
  } catch {
    return value;
  }
};

export const PublicActivationPage = () => {
  const { packId } = useParams<{ packId: string }>();
  const accessToken = capturePrivateAccessToken() || "";
  const { data, isError, isFetching, isLoading, refetch } =
    usePublicActivationPack(packId);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const activationData = useMemo(() => {
    if (!data || !accessToken) return data;
    return {
      ...data,
      targetUrl: addPrivateAccessToken(data.targetUrl, accessToken),
      channels: data.channels.map((channel) => ({
        ...channel,
        url:
          channel.type === "web_widget"
            ? addPrivateAccessToken(channel.url, accessToken)
            : channel.url,
      })),
    };
  }, [accessToken, data]);

  const smartLink = useMemo(() => {
    const url = new URL(`/get/${packId}`, SITE_URL);
    url.searchParams.set("intent", "install");
    url.searchParams.set("source", "presentation");
    if (accessToken) addPrivateAccessTokenFragment(url, accessToken);
    return url.toString();
  }, [accessToken, packId]);

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] bg-[#F9FAFC] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#4E9CFF] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-[100dvh] bg-[#F9FAFC] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-[#222B45] mb-2">
            We couldn't load this page
          </h1>
          <p className="text-[#64748B] mb-6">
            Something went wrong while loading it. Please try again.
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex items-center px-5 py-2.5 rounded-xl text-white font-semibold disabled:opacity-60"
            style={{
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          >
            {isFetching && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!activationData) {
    return (
      <div className="min-h-[100dvh] bg-[#F9FAFC] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-[#222B45] mb-2">
            This page isn't available
          </h1>
          <p className="text-[#64748B] mb-6">
            The Genie you're looking for may be private or no longer active.
          </p>
          <a
            href="/"
            className="inline-flex items-center px-5 py-2.5 rounded-xl text-white font-semibold"
            style={{
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          >
            Go to Help Genie
          </a>
        </div>
      </div>
    );
  }

  return (
    <Presentation
      data={activationData}
      smartLink={smartLink}
      packId={packId || ""}
      scrollerRef={scrollerRef}
      active={active}
      setActive={setActive}
      copied={copied}
      setCopied={setCopied}
    />
  );
};

interface PresentationProps {
  data: PublicActivationDTO;
  smartLink: string;
  packId: string;
  scrollerRef: React.RefObject<HTMLDivElement>;
  active: number;
  setActive: (n: number) => void;
  copied: boolean;
  setCopied: (b: boolean) => void;
}

const EASE = [0.22, 1, 0.36, 1] as const;

const Presentation = ({
  data,
  smartLink,
  packId,
  scrollerRef,
  active,
  setActive,
  copied,
  setCopied,
}: PresentationProps) => {
  const primary = data.branding.primaryColor || "#4E9CFF";
  const secondary = data.branding.secondaryColor || "#FF4DB8";
  const gradient = `linear-gradient(150deg, ${primary}, ${secondary})`;
  const content = useMemo(
    () => resolveActivationPresentationContent(data),
    [data],
  );
  const phoneChannel = data.channels.find((c) => c.type === "phone");

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setActive(Math.round(el.scrollTop / el.clientHeight));
  };

  const goTo = (i: number) => {
    const el = scrollerRef.current?.children[i] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.targetUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  // Content blooms as each section scrolls into view — the same dissolve-and-settle
  // language as the welcome screen, spread the length of the deck. Spread onto a
  // motion element (not a wrapper component) so scroll re-renders never remount it.
  const reveal = (delay = 0, y = 22) => ({
    initial: { opacity: 0, y, filter: "blur(8px)" as const },
    whileInView: { opacity: 1, y: 0, filter: "blur(0px)" as const },
    viewport: { root: scrollerRef, amount: 0.35 },
    transition: { duration: 0.55, ease: EASE, delay },
  });

  const Logo = ({ size = 80 }: { size?: number }) =>
    data.branding.logoUrl ? (
      <img
        src={data.branding.logoUrl}
        alt={data.name}
        style={{ width: size, height: size }}
        className="rounded-2xl bg-white object-contain p-2.5 shadow-lg"
      />
    ) : (
      <div
        style={{ width: size, height: size }}
        className="rounded-2xl bg-white/90 flex items-center justify-center shadow-lg"
      >
        <span className="text-3xl font-bold" style={{ color: primary }}>
          {data.name.charAt(0).toUpperCase()}
        </span>
      </div>
    );

  return (
    <>
      <Helmet>
        <title>
          {data.targetType === "portal"
            ? `Explore ${data.name}`
            : `Meet ${data.name}`}{" "}
          · Help Genie
        </title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content={content.promiseBody} />
        <meta
          property="og:title"
          content={
            data.targetType === "portal"
              ? `Explore ${data.name}`
              : `Meet ${data.name}`
          }
        />
        <meta property="og:description" content={content.promiseBody} />
        <meta property="og:url" content={`${SITE_URL}/activate/${packId}`} />
        {data.branding.logoUrl && (
          <meta property="og:image" content={data.branding.logoUrl} />
        )}
      </Helmet>

      <button
        type="button"
        onClick={() => goTo(SECTION_COUNT - 1)}
        className="fixed right-4 top-4 z-40 inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2.5 text-sm font-semibold shadow-lg transition-transform hover:scale-105 sm:right-6 sm:top-6"
        style={{ borderColor: `${primary}35`, color: primary }}
        aria-label="Jump to app download options"
      >
        <Smartphone className="h-4 w-4" />
        Get the app
      </button>

      {/* Progress dots */}
      <div className="hidden sm:flex fixed right-6 top-1/2 -translate-y-1/2 z-30 flex-col gap-3">
        {Array.from({ length: SECTION_COUNT }).map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to section ${i + 1}`}
            onClick={() => goTo(i)}
            className="rounded-full transition-all"
            style={{
              width: active === i ? 10 : 8,
              height: active === i ? 10 : 8,
              background: active === i ? primary : "rgba(100,116,139,0.35)",
            }}
          />
        ))}
      </div>

      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="h-[100dvh] overflow-y-auto snap-y snap-mandatory scroll-smooth"
      >
        {/* 0 — Hero */}
        <section
          className="snap-start min-h-[100dvh] flex flex-col items-center justify-center text-center px-6 text-white relative overflow-hidden"
          style={{ background: gradient }}
        >
          <div
            className="absolute inset-0 opacity-25 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 25% 15%, #fff 0%, transparent 45%)",
            }}
          />
          <GenieMist
            brandColor="#FFFFFF"
            secondaryColor="#FFFFFF"
            intensity={0.5}
          />
          <motion.div
            className="relative"
            initial={{ opacity: 0, y: 18, filter: "blur(14px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="flex justify-center">
              <Logo size={92} />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white mt-7 backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              {content.statusLabel}
            </div>
            <div className="text-xs font-semibold tracking-[0.24em] uppercase text-white/80 mt-5 mb-2">
              {content.heroEyebrow}
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">{data.name}</h1>
            {data.tagline && (
              <p className="text-xl text-white/90 max-w-xl mx-auto">
                {data.tagline}
              </p>
            )}
          </motion.div>
          <button
            type="button"
            onClick={() => goTo(1)}
            className="absolute bottom-10 flex flex-col items-center gap-1 text-white/80 hover:text-white transition-colors"
          >
            <span className="text-xs font-medium uppercase tracking-wide">
              Take a look
            </span>
            <ChevronDown className="w-6 h-6 animate-bounce" />
          </button>
        </section>

        {/* 1 — The promise */}
        <section className="snap-start min-h-[100dvh] flex flex-col items-center justify-center text-center px-6 bg-white">
          <div className="max-w-2xl">
            <motion.div
              {...reveal(0)}
              className="text-xs font-semibold tracking-[0.24em] uppercase mb-4"
              style={{ color: primary }}
            >
              Why it matters
            </motion.div>
            <motion.h2
              {...reveal(0.08)}
              className="text-3xl md:text-5xl font-bold text-[#222B45] leading-tight"
            >
              {content.promiseHeadline}
            </motion.h2>
            <motion.p {...reveal(0.16)} className="text-lg text-[#64748B] mt-6">
              {content.promiseBody}
            </motion.p>
            <motion.div
              {...reveal(0.24)}
              className="mt-8 flex flex-wrap items-center justify-center gap-2.5"
            >
              {content.highlights.map((pill) => (
                <span
                  key={pill}
                  className="rounded-full px-4 py-1.5 text-sm font-medium"
                  style={{ backgroundColor: `${primary}12`, color: primary }}
                >
                  {pill}
                </span>
              ))}
            </motion.div>
          </div>
        </section>

        {/* 2 — What it does */}
        <section className="snap-start min-h-[100dvh] flex flex-col items-center justify-center px-6 py-16 bg-[#F9FAFC]">
          <div className="max-w-4xl w-full">
            <div className="text-center mb-10">
              <motion.div
                {...reveal(0)}
                className="text-xs font-semibold tracking-[0.24em] uppercase mb-3"
                style={{ color: primary }}
              >
                What it does
              </motion.div>
              <motion.h2
                {...reveal(0.08)}
                className="text-3xl md:text-4xl font-bold text-[#222B45]"
              >
                {content.capabilityHeadline}
              </motion.h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {content.capabilities.map((c, i) => (
                <motion.div
                  key={c.title}
                  {...reveal(0.08 + i * 0.06)}
                  className="bg-white rounded-2xl border border-[#E5EAF0] p-5 flex items-start gap-4"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${primary}12` }}
                  >
                    <GenieIcon
                      name={c.genieIcon}
                      size={26}
                      color={primary}
                      secondaryColor={secondary}
                      gradientEnabled
                    />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{c.title}</div>
                    <p className="text-sm text-gray-600 mt-1">{c.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 3 — What you can ask it */}
        <section
          className="snap-start min-h-[100dvh] flex flex-col items-center justify-center px-6 py-16 text-white relative overflow-hidden"
          style={{ background: gradient }}
        >
          <GenieMist
            brandColor="#FFFFFF"
            secondaryColor="#FFFFFF"
            intensity={0.45}
          />
          <div className="relative max-w-md w-full">
            <div className="text-center mb-8">
              <motion.div
                {...reveal(0)}
                className="text-xs font-semibold tracking-[0.24em] uppercase mb-3 text-white/80"
              >
                {content.examplesEyebrow}
              </motion.div>
              <motion.h2
                {...reveal(0.08)}
                className="text-3xl md:text-4xl font-bold"
              >
                {content.examplesHeadline}
              </motion.h2>
            </div>
            <div className="bg-white/12 rounded-3xl p-4 backdrop-blur-md border border-white/15 space-y-3">
              {content.welcomeMessage && (
                <motion.div {...reveal(0.1, 14)} className="flex justify-start">
                  <span className="max-w-[90%] rounded-2xl rounded-bl-md bg-white/85 px-4 py-2.5 text-[15px] font-medium text-[#222B45] shadow-sm">
                    {content.welcomeMessage}
                  </span>
                </motion.div>
              )}
              {content.examplePrompts.map((q, i) => (
                <motion.div
                  key={q}
                  {...reveal(0.12 + i * 0.1, 14)}
                  className="flex justify-end"
                >
                  <span className="max-w-[85%] rounded-2xl rounded-br-md bg-white px-4 py-2.5 text-[15px] font-medium text-[#222B45] shadow-sm">
                    {q}
                  </span>
                </motion.div>
              ))}
              <motion.div
                {...reveal(0.12 + content.examplePrompts.length * 0.1, 14)}
                className="flex justify-start"
              >
                <span className="inline-flex items-center gap-2 rounded-2xl rounded-bl-md bg-white/85 px-4 py-3 text-[#222B45] shadow-sm">
                  <span className="text-sm font-semibold">
                    {data.targetType === "portal"
                      ? `Open ${data.name} to continue`
                      : `${data.name} is ready`}
                  </span>
                  {data.targetType === "portal" ? (
                    <ArrowRight
                      className="h-4 w-4"
                      style={{ color: primary }}
                    />
                  ) : (
                    <span className="flex items-center gap-1">
                      {[0, 1, 2].map((d) => (
                        <motion.span
                          key={d}
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: primary }}
                          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            delay: d * 0.18,
                            ease: "easeInOut",
                          }}
                        />
                      ))}
                    </span>
                  )}
                </span>
              </motion.div>
            </div>
            <motion.p
              {...reveal(0.2)}
              className="text-center text-sm text-white/80 mt-6"
            >
              {content.examplesFootnote}
            </motion.p>
          </div>
        </section>

        {/* 4 — How it works */}
        <section className="snap-start min-h-[100dvh] flex flex-col items-center justify-center px-6 py-16 bg-white">
          <div className="max-w-3xl w-full text-center">
            <motion.div
              {...reveal(0)}
              className="text-xs font-semibold tracking-[0.24em] uppercase mb-3"
              style={{ color: primary }}
            >
              How it works
            </motion.div>
            <motion.h2
              {...reveal(0.08)}
              className="text-3xl md:text-4xl font-bold mb-10 text-[#222B45]"
            >
              {content.stepsHeadline}
            </motion.h2>
            <div className="grid gap-6 sm:grid-cols-3">
              {content.steps.map((s, i) => (
                <motion.div
                  key={s.title}
                  {...reveal(0.1 + i * 0.1)}
                  className="rounded-2xl border border-[#E5EAF0] bg-[#F9FAFC] p-6"
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: `${primary}12` }}
                  >
                    <GenieIcon
                      name={s.genieIcon}
                      size={30}
                      color={primary}
                      secondaryColor={secondary}
                      gradientEnabled
                    />
                  </div>
                  <div
                    className="text-sm font-bold mb-1"
                    style={{ color: primary }}
                  >
                    STEP {i + 1}
                  </div>
                  <div className="font-semibold text-lg text-[#222B45]">
                    {s.title}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{s.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 5 — Ways to reach it */}
        <section className="snap-start min-h-[100dvh] flex flex-col items-center justify-center px-6 py-16 bg-[#F9FAFC]">
          <div className="max-w-3xl w-full">
            <div className="text-center mb-10">
              <motion.div
                {...reveal(0)}
                className="text-xs font-semibold tracking-[0.24em] uppercase mb-3"
                style={{ color: primary }}
              >
                Try it now
              </motion.div>
              <motion.h2
                {...reveal(0.08)}
                className="text-3xl md:text-4xl font-bold text-[#222B45]"
              >
                {content.channelsHeadline}
              </motion.h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {data.channels.map((channel: PublicActivationChannel, i) => {
                const meta =
                  CHANNEL_META[channel.type] || CHANNEL_META.web_widget;
                return (
                  <motion.a
                    key={channel.type}
                    {...reveal(0.1 + i * 0.08)}
                    href={channel.url}
                    className="group p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] block"
                  >
                    <div className="rounded-[14px] bg-white px-5 py-5 h-full flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${primary}15` }}
                      >
                        <GenieIcon
                          name={meta.genieIcon}
                          size={26}
                          color={primary}
                          secondaryColor={secondary}
                          gradientEnabled
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-900">
                          {channel.label}
                        </div>
                        <div
                          className="text-sm font-medium flex items-center gap-1"
                          style={{ color: primary }}
                        >
                          {data.targetType === "portal" &&
                          channel.type === "web_widget"
                            ? "Open portal"
                            : meta.cta}
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </motion.a>
                );
              })}
            </div>
            <motion.div {...reveal(0.2)} className="text-center mt-8">
              <a
                href={data.targetUrl}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white font-semibold shadow-lg hover:scale-105 transition-transform"
                style={{ background: gradient }}
              >
                {content.primaryActionLabel}
                <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* 6 — Get set up (finale) */}
        <section
          className="snap-start min-h-[100dvh] flex flex-col items-center justify-center px-6 py-16 text-white relative overflow-hidden"
          style={{ background: gradient }}
        >
          <GenieMist
            brandColor="#FFFFFF"
            secondaryColor="#FFFFFF"
            intensity={0.5}
          />
          <div className="relative max-w-3xl w-full text-center">
            <motion.div
              {...reveal(0)}
              className="text-xs font-semibold tracking-[0.24em] uppercase mb-3 text-white/80"
            >
              You're ready
            </motion.div>
            <motion.h2
              {...reveal(0.08)}
              className="text-3xl md:text-5xl font-bold mb-3"
            >
              {content.finaleHeadline}
            </motion.h2>
            <motion.p {...reveal(0.16)} className="text-lg text-white/90 mb-10">
              {content.finaleBody}
            </motion.p>

            <div className="grid gap-5 sm:grid-cols-3 text-left">
              {/* Get the app */}
              <motion.div
                {...reveal(0.2)}
                className="bg-white rounded-2xl p-6 flex flex-col"
              >
                <div className="font-semibold text-gray-900 mb-1">
                  Get the app
                </div>
                <p className="text-sm text-gray-600 mb-4">{content.appBody}</p>
                <div className="mt-auto space-y-3">
                  <a
                    href={smartLink}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-semibold text-sm shadow-sm hover:scale-[1.02] transition-transform"
                    style={{ background: gradient }}
                  >
                    <Smartphone className="w-4 h-4" />
                    Get the app &amp; open {data.name}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <div className="text-xs text-gray-500">
                    Or choose a store directly
                  </div>
                  <div className="flex flex-col gap-2">
                    <a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:scale-105 transition-transform"
                    >
                      <img
                        src={APP_STORE_BADGE_SRC}
                        alt="Download on the App Store"
                        className="h-10"
                      />
                    </a>
                    <a
                      href={GOOGLE_PLAY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:scale-105 transition-transform"
                    >
                      <img
                        src={GOOGLE_PLAY_BADGE_SRC}
                        alt="Get it on Google Play"
                        className="h-10"
                      />
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Scan the QR */}
              <motion.div
                {...reveal(0.28)}
                className="bg-white rounded-2xl p-6 flex flex-col items-center text-center"
              >
                <div className="font-semibold text-gray-900 mb-1 self-start">
                  Scan the code
                </div>
                <p className="text-sm text-gray-600 mb-4 self-start">
                  Opens the app or{" "}
                  {data.targetType === "portal"
                    ? "the portal"
                    : "the conversation"}
                  .
                </p>
                <div className="mt-auto p-3 rounded-xl border border-gray-100">
                  <QRCodeSVG
                    value={smartLink}
                    size={120}
                    level="H"
                    fgColor="#222B45"
                  />
                </div>
              </motion.div>

              {/* Save the link */}
              <motion.div
                {...reveal(0.36)}
                className="bg-white rounded-2xl p-6 flex flex-col"
              >
                <div className="font-semibold text-gray-900 mb-1">
                  Save the link
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Bookmark it for whenever you need it.
                </p>
                <div className="mt-auto space-y-2">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm"
                    style={{ background: gradient }}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" /> Copy link
                      </>
                    )}
                  </button>
                  {phoneChannel && (
                    <a
                      href={phoneChannel.url}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 font-semibold text-sm"
                      style={{ borderColor: `${primary}40`, color: primary }}
                    >
                      Call instead
                    </a>
                  )}
                </div>
              </motion.div>
            </div>

            <div className="mt-10 text-white/80 text-sm">
              Powered by Help Genie
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PublicActivationPage;
